// src/views/UnitsView.jsx
import React, { useState } from 'react';
import { ArrowLeft, BookOpen, Lock, Zap, ChevronRight, X, CheckCircle2, AlertCircle, GraduationCap } from 'lucide-react';
import { playSound } from '../utils/sounds';
import Mascot from '../components/Mascot';
import MasteryZone from '../components/hub/MasteryZone';

export default function UnitsView({ module, userProgress, openUnit, setView, onBuyContent }) {
  const completedSets = userProgress.completedSets || [];
  const unlockedContent = userProgress.unlockedContent || [];
  const [isAnimatingUnlock, setIsAnimatingUnlock] = useState(false);
  
  const SETS_REQUIRED = 5;
  const UNIT_COST = 200;
const MODULE_SPECIAL_COST = 300;
  const currentSkin = userProgress?.currentSkin || 'bribark';

  const dollyXpMsgs = [
    "Ups… te faltan un poquito de XP 💕",
    "Aún no alcanza, pero ya casi ✨",
    "Un poco más de práctica y será tuyo 💖",
    "Todavía no, pero vas muy bien 🌸",
    "Sigamos jugando para ganar más XP 😊"
  ];

  const bribarkXpMsgs = [
    "Eh… te faltan XP 😏",
    "Casi, pero no… a seguir dandole! 🐾",
    "Ese objeto pide más XP, campeón 💪",
    "No alcanza… toca regresar a la \"chambing\" un poco 😈",
    "Más XP primero, luego hablamos 😎"
  ];
const normalUnits = module.units.filter(unit => 
    !unit.id.endsWith('-final-reading') && !unit.id.endsWith('-final-review')
  );

  const moduleReading = module.units.find(unit => unit.id.endsWith('-final-reading'));
  const moduleReview = module.units.find(unit => unit.id.endsWith('-final-review'));
  const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const totalNormalUnits = normalUnits.length;
  const completedNormalUnits = normalUnits.filter(unit => {
    const currentSetsDone = unit.sets.filter(s => completedSets.includes(s.id)).length;
    return currentSetsDone === unit.sets.length; // Unidad 100% completa
  }).length;
  const isModuleComplete = completedNormalUnits === totalNormalUnits;

  const [unlockConfirmModal, setUnlockConfirmModal] = useState(null);
  const [xpErrorModal, setXpErrorModal] = useState(null);

  const handleUnlockClick = (unitId, unitName, cost) => {
    setUnlockConfirmModal({ unitId, unitName, cost });
  };

  const confirmUnlockPurchase = () => {
    if (!unlockConfirmModal) return;

    const { unitId, unitName, cost } = unlockConfirmModal;

    if ((userProgress.xp || 0) < cost) {
      const missingXp = Math.max(cost - (userProgress.xp || 0), 0);
      const msg = currentSkin.includes('dolly') ? pickRandom(dollyXpMsgs) : pickRandom(bribarkXpMsgs);

      setUnlockConfirmModal(null);
      setXpErrorModal({
        title: "¡Te faltan XP!",
        message: msg,
        missingXp
      });
      return;
    }

    playSound('book');
    setIsAnimatingUnlock(true);
    setUnlockConfirmModal(null);

    setTimeout(() => {
      const success = onBuyContent(unitId, cost);
      if (success) {
        playSound('wow');
      }
      setIsAnimatingUnlock(false);
    }, 2000);
  };

  return (
    <div className="relative min-h-screen pb-24 bg-slate-50">
      <div className="space-y-6 animate-in slide-in-from-right duration-500">
        
        {/* HEADER PREMIUM CON BOTÓN MEJORADO */}
<div className="relative mb-6">
  {/* Botón de retorno - Z-index alto y área grande */}
  <button
    onClick={() => setView('modules')}
    className="absolute top-4 left-4 z-50 p-3.5 bg-white rounded-2xl hover:bg-indigo-50 transition-all shadow-xl hover:shadow-2xl hover:scale-110 active:scale-95 border-2 border-indigo-100"
  >
    <ArrowLeft size={22} className="text-indigo-600" strokeWidth={2.5} />
  </button>

  {/* Header visual */}
  <div className="bg-gradient-to-br from-indigo-600 via-indigo-600 to-purple-700 p-6 pt-16 rounded-b-[2.5rem] text-white shadow-2xl shadow-indigo-200 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
    
    <div className="text-center relative z-10">
      <h1 className="text-2xl font-black tracking-tight drop-shadow-lg">{module.title || module.name}</h1>
      <p className="text-indigo-200 font-bold text-sm mt-1">Ruta de Aprendizaje</p>
    </div>
  </div>
</div>

{/* LISTA DE UNIDADES - ESTILO MAPA */}
<div className="px-4 max-w-lg mx-auto relative">

  {/* Línea vertical conectora */}
  {/* Línea vertical conectora con glow */}
<div className="absolute left-1/2 top-0 bottom-0 w-1 -translate-x-1/2 z-0"
  style={{
    background: 'repeating-linear-gradient(to bottom, #6366f1 0px, #6366f1 10px, transparent 10px, transparent 20px)',
    filter: 'drop-shadow(0 0 4px rgba(99,102,241,0.8))'
  }}
/>

  <div className="relative z-10 flex flex-col items-center gap-2">
    {normalUnits.map((unit, index) => {
      let progressInPrev = SETS_REQUIRED;
      let prevUnitTitle = "";

      if (index > 0) {
        const prevUnit = normalUnits[index - 1];
        prevUnitTitle = prevUnit.title;
        progressInPrev = prevUnit.sets.filter(s => completedSets.includes(s.id)).length;
      }

      const isPurchased = unlockedContent.includes(unit.id);
      const isRequirementMet = progressInPrev >= SETS_REQUIRED;
      const isUnlocked = index === 0 || isRequirementMet || isPurchased;
      const currentSetsDone = unit.sets.filter(s => completedSets.includes(s.id)).length;
      const totalSets = unit.sets.length;
      const percentage = Math.round((currentSetsDone / totalSets) * 100);
      const progressPercent = (progressInPrev / SETS_REQUIRED) * 100;
      const remaining = Math.max(SETS_REQUIRED - progressInPrev, 0);
      const isHero = isUnlocked;;

      return (
        <div key={unit.id} className="w-full flex flex-col items-center">

          {/* === NODO HERO (primera unidad o activa) === */}
          {isHero && isUnlocked ? (
            <div className="w-full flex flex-col items-center my-4">
              {/* Glow ring */}
              <div
                onClick={() => openUnit(unit)}
                className="relative w-52 h-52 rounded-full flex flex-col items-center justify-center cursor-pointer"
                style={{
  background: 'radial-gradient(circle, #ffffff 50%, #eef2ff 100%)',
  boxShadow: '0 0 0 8px rgba(99,102,241,0.2), 0 0 0 16px rgba(99,102,241,0.1), 0 0 30px rgba(99,102,241,0.3)',
  border: '5px solid rgba(99,102,241,0.7)'
}}
              >
                {/* Partículas decorativas */}
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-purple-400 rounded-full opacity-70 animate-pulse" />
                <div className="absolute top-4 -left-3 w-3 h-3 bg-indigo-300 rounded-full opacity-60 animate-pulse" style={{animationDelay:'0.5s'}}/>
                <div className="absolute -bottom-1 right-6 w-2 h-2 bg-pink-400 rounded-full opacity-50 animate-pulse" style={{animationDelay:'1s'}}/>
<div className="relative w-16 h-16 rounded-full mb-2
  flex items-center justify-center
  border-4 border-indigo-300"
  style={{
    background: 'linear-gradient(135deg, #6366f1, #3b82f6)',
    boxShadow: '0 4px 20px rgba(99,102,241,0.6), 0 0 0 4px rgba(99,102,241,0.2)'
  }}>
  {/* Brillo superior */}
  <div className="absolute top-0 left-0 w-full h-1/2 bg-white/30 rounded-t-full pointer-events-none" />
<div className="absolute top-1 left-2 w-2.5 h-2.5 bg-white/50 rounded-full blur-sm pointer-events-none" />
  {/* Punto de luz */}
  <div className="absolute top-1.5 left-3 w-3 h-3 bg-white/50 rounded-full blur-sm pointer-events-none" />
  <span className="text-3xl relative z-10 drop-shadow-lg">{unit.icon || '💬'}</span>
</div>
                <p className="text-indigo-800 font-black text-sm text-center px-4 leading-tight">
                  Unit {index + 1}
                </p>
                <p className="text-indigo-400 text-[10px] font-bold text-center px-4 mt-0.5">
                  Completa {SETS_REQUIRED} sets en Unit {index + 1}
                </p>

                {/* Mini progress ring */}
                <div className="mt-2 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200">
                  <p className="text-indigo-600 font-black text-xs">{currentSetsDone}/{totalSets}</p>
                </div>

                {/* Badge XP */}
                <div className="absolute -top-3 -right-3 bg-indigo-600 text-white px-3 py-1.5 rounded-full text-[10px] font-black shadow-lg border-2 border-indigo-400">
  ▶ Inicia ahora
</div>
              </div>

              {/* Botón desbloqueo hero */}
              {index > 0 && !isUnlocked && (
  <button onClick={(e) => { e.stopPropagation(); handleUnlockClick(unit.id, unit.title, UNIT_COST); }}
      
                className="mt-4 flex items-center gap-2 px-8 py-3.5 rounded-full font-black text-white text-sm shadow-xl transition-all active:scale-95 border-b-4 border-amber-600"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)' }}
              >
                <span>▶</span> Desbloquear por {UNIT_COST} XP
              </button>
              )}
          
            </div>
          ) : (

  /* === TARJETA DESBLOQUEADA / BLOQUEADA === */

  <div className="w-full flex flex-col items-center">

    {/* Nodo conector */}
    <div className={`w-6 h-6 rounded-full border-4 z-10 shadow-md mb-1
      ${isUnlocked
        ? 'bg-indigo-500 border-indigo-300 shadow-indigo-300'
        : 'bg-slate-200 border-slate-300'
      }`}
      style={isUnlocked ? {boxShadow: '0 0 10px rgba(99,102,241,0.6)'} : {}}
    />

    <div
      onClick={() => isUnlocked && openUnit(unit)}
      className={`w-full rounded-3xl border-2 transition-all overflow-hidden
        ${isUnlocked
          ? 'bg-white border-indigo-200 cursor-pointer hover:border-indigo-400 hover:shadow-xl hover:-translate-y-1'
          : 'bg-slate-100/80 border-slate-200'
        }`}
      style={isUnlocked ? {
        boxShadow: '0 4px 20px rgba(99,102,241,0.15), 0 0 0 1px rgba(99,102,241,0.1)'
      } : {}}
    >
      <div className="p-4 space-y-3 relative">

        {/* Badge XP flotante */}
        <div className="absolute top-3 right-3 bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full text-[10px] font-black flex items-center gap-1 shadow border border-amber-200">
          <Zap size={9} fill="currentColor" /> {UNIT_COST} XP
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 pr-16">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-md shrink-0 relative overflow-hidden
            ${isUnlocked
              ? 'bg-gradient-to-br from-indigo-400 to-blue-500'
              : 'bg-gradient-to-br from-slate-300 to-slate-400'
            }`}
            style={isUnlocked ? {
  background: 'radial-gradient(circle, #ffffff 50%, #eef2ff 100%)',
  boxShadow: '0 0 0 8px rgba(99,102,241,0.2), 0 0 0 16px rgba(99,102,241,0.1)',
  border: '5px solid rgba(99,102,241,0.7)'
} : {
  background: 'radial-gradient(circle, #1e293b 40%, #0f172a 100%)',
  boxShadow: '0 0 0 8px rgba(0,0,0,0.2), 0 0 0 16px rgba(0,0,0,0.1)',
  border: '5px solid rgba(100,116,139,0.5)',
  filter: 'grayscale(0.8)'
}}
          >
            {/* Brillo superior */}
            {isUnlocked && (
              <div className="absolute top-0 left-0 w-full h-1/2 bg-white/30 rounded-t-2xl pointer-events-none" />
            )}
            <span className="relative z-10">
              {isUnlocked ? (unit.icon || '📖') : <Lock size={22} className="text-slate-100" />}
            </span>
          </div>

          <div>
            {!isUnlocked && (
              <p className="text-[9px] font-black uppercase text-slate-400 tracking-wider mb-0.5">
                🔒 Bloqueado
              </p>
            )}
            <p className={`font-black text-sm leading-tight
              ${isUnlocked ? 'text-slate-800' : 'text-slate-500'}`}>
              {unit.title}
            </p>
            <p className="text-[10px] font-bold text-slate-400 uppercase">{totalSets} lecciones</p>
            {/* Estrellas */}
            <div className="flex gap-0.5 mt-1">
              {[1,2,3].map(s => (
                <span key={s} className={`text-xs ${percentage >= s*33 ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
              ))}
            </div>
          </div>
        </div>

        {isUnlocked ? (
          <div className="space-y-1.5">
            <div className="flex justify-between text-[10px] font-black text-slate-400">
              <span>Tu progreso</span>
              <span className="text-indigo-600 font-black">{currentSetsDone}/{totalSets}</span>
            </div>
            {/* Barra con glow */}
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden border border-indigo-100">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${percentage}%`,
                  background: 'linear-gradient(90deg, #6366f1, #3b82f6)',
                  boxShadow: '0 0 8px rgba(99,102,241,0.6)'
                }}
              />
            </div>
            {/* Flecha */}
            <div className="flex justify-end pt-1">
              <ChevronRight size={20} className="text-indigo-400" strokeWidth={3} />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-3 border border-slate-200 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-black text-slate-500 truncate flex-1">
                Completa {SETS_REQUIRED} sets en "{prevUnitTitle}"
              </p>
              <div className="flex gap-0.5 ml-2">
                {[1,2,3].map(s => (
                  <span key={s} className={`text-[10px] ${(progressInPrev/SETS_REQUIRED)*3 >= s ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
                ))}
              </div>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-400 to-amber-500 rounded-full"
                style={{ width: `${Math.min(progressPercent, 100)}%` }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-orange-500">{progressInPrev}/{SETS_REQUIRED}</span>
              {(() => {
                const prevUnit = normalUnits[index - 1];
                const prevIsPurchased = unlockedContent.includes(prevUnit?.id);
                const prevIsUnlocked = index === 1 || prevIsPurchased ||
                  (index >= 2 && normalUnits[index-2]?.sets
                    .filter(s => completedSets.includes(s.id)).length >= SETS_REQUIRED);
                return prevIsUnlocked ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); openUnit(prevUnit); }}
                    className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100 hover:bg-indigo-100 transition-all"
                  >
                    Ir a Unit {index}
                  </button>
                ) : (
                  <span className="text-[10px] font-bold text-slate-300 px-2">Unit {index} bloqueada</span>
                );
              })()}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); handleUnlockClick(unit.id, unit.title, UNIT_COST); }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black text-white border-b-2 border-amber-600 active:border-b-0 transition-all"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)' }}
            >
              <Zap size={13} fill="currentColor" /> Desbloquear por {UNIT_COST} XP
           </button>
          </div>
      
          )}
        </div>
        </div>
        </div>
        )}
      </div>
      );
    })}
  </div>
</div>


        {/* MASTERY ZONE */}   
         
        <div className="px-4 max-w-lg mx-auto mt-4">   
{(moduleReview || moduleReading) && (
            <MasteryZone
              unit={{
                id: module.id,
                title: module.title,
                reviewSet: moduleReview,
                readingSet: moduleReading
              }}
              unlockedContent={unlockedContent}
              isUnitFinished={isModuleComplete}
              totalSets={totalNormalUnits}
              completedCount={completedNormalUnits}
             startSet={(set) => {
  openUnit(set);
}}

              handleUnlockClick={handleUnlockClick}
              SPECIAL_COST={MODULE_SPECIAL_COST}
              isModuleLevel={true}
              moduleTitle={module.title}
              totalUnits={totalNormalUnits}
              completedUnits={completedNormalUnits}
           />
  
  )}
    </div>
    </div>

 

      {/* MODAL DE CONFIRMACIÓN */}
      {unlockConfirmModal && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-sm rounded-[2.5rem] p-8 text-center relative shadow-2xl animate-in zoom-in-95 duration-300 border-4 border-indigo-100">
            
            <button 
              onClick={() => setUnlockConfirmModal(null)} 
              className="absolute top-4 right-4 p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
            >
              <X size={20} className="text-slate-400" />
            </button>

            <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 drop-shadow-2xl">
              <Mascot type={currentSkin} />
            </div>

            <div className="mt-12 space-y-4">
              <h2 className="text-2xl font-black text-slate-800 mb-2 leading-tight">
                ¿Desbloquear Unidad?
              </h2>

              <p className="text-slate-500 font-bold mb-4 text-sm">
                Usarás tus puntos para entrar a: <br />
                <span className="text-indigo-600">"{unlockConfirmModal.unitName}"</span>
              </p>

              <div className="bg-amber-50 p-5 rounded-3xl mb-6 border-2 border-amber-100/50">
                <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1">Costo de acceso</p>
                <div className="flex items-center justify-center gap-2 text-3xl font-black text-amber-500">
                  <Zap fill="currentColor" className="text-amber-400" /> 
                  {unlockConfirmModal.cost} XP
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setUnlockConfirmModal(null)}
                  className="py-3 rounded-2xl font-black text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors border-b-4 border-slate-200 active:border-b-0 active:translate-y-1"
                >
                  CANCELAR
                </button>
                <button
                  onClick={confirmUnlockPurchase}
                  className="py-3 rounded-2xl font-black text-white bg-indigo-600 hover:bg-indigo-500 transition-all border-b-4 border-indigo-800 shadow-lg shadow-indigo-200 active:border-b-0 active:translate-y-1"
                >
                  CONFIRMAR
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FALTAN XP */}
      {xpErrorModal && (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-[2.5rem] p-8 w-full max-w-sm text-center shadow-2xl animate-in zoom-in duration-200 border-4 border-indigo-100 relative">
            
            <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 pointer-events-none">
              <Mascot type={currentSkin} />
            </div>

            <div className="mt-12 space-y-4">
              <h3 className="text-2xl font-black text-slate-700 leading-tight">
                {xpErrorModal.title}
              </h3>

              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
                <p className="text-sm font-black text-slate-700 italic">
                  "{xpErrorModal.message}"
                </p>
                <p className="text-xs font-bold text-slate-400 mt-2 uppercase tracking-widest">
                  Te faltan {xpErrorModal.missingXp} XP
                </p>
              </div>

              <button
                onClick={() => setXpErrorModal(null)}
                className="w-full py-3 rounded-2xl font-black text-white bg-slate-800 hover:bg-slate-700 transition-colors border-b-4 border-slate-900 active:border-b-0 active:translate-y-1"
              >
                ENTENDIDO
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ANIMACIÓN DE DESBLOQUEO */}
      {isAnimatingUnlock && (
        <div className="fixed inset-0 z-[200] bg-indigo-950/90 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-500">
          
          <div className="absolute w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] lg:w-[800px] lg:h-[800px] bg-[conic-gradient(from_0deg,transparent_0deg,rgba(251,191,36,0.3)_20deg,transparent_50deg)] animate-spin-slow rounded-full opacity-60" />

          <div className="relative z-10 w-full max-w-xs sm:max-w-md flex flex-col items-center">
            <div className="animate-magic-book">
              <BookOpen 
                className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 text-amber-400 drop-shadow-[0_0_40px_rgba(251,191,36,0.7)]" 
                strokeWidth={1.5} 
              />
            </div>

            <p className="mt-8 sm:mt-12 text-white font-black text-lg sm:text-xl lg:text-2xl tracking-[0.2em] sm:tracking-[0.3em] animate-pulse uppercase drop-shadow-lg text-center px-4">
              Liberando Sabiduría
            </p>
          </div>
        </div>
      )}
  </div>
  );
}
